import React, { useState, useEffect } from 'react';
import LORManagerSelector from './LORManagerSelector';
import Icon from '../../components/Icon';

export default function LORByePlanner({ api }) {
  const [rosterId, setRosterId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!rosterId) return;
    setLoading(true);
    setError(null);
    fetch(`${api}/api/lor/intel/bye-planner/${rosterId}`)
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError('Could not load bye planner data.'); setLoading(false); });
  }, [api, rosterId]);

  return (
    <div>
      <div className="lor-page-header">
        <h2 className="lor-page-title">🗓️ Bye Week Planner</h2>
        <LORManagerSelector api={api} onSelect={setRosterId} selectedRosterId={rosterId} />
      </div>

      {!rosterId && (
        <div className="lor-intel-empty">
          <div className="lor-intel-empty-icon">🗓️</div>
          <p>Select your team to see bye week coverage analysis.</p>
        </div>
      )}

      {rosterId && loading && <div className="lor-loading">Checking bye weeks…</div>}
      {rosterId && error && <div className="lor-empty">{error}</div>}

      {data && !loading && (
        <>
          <div className="lor-intel-meta">
            Week {data.current_week} · <span className="lor-generated-badge">rules-based</span>
          </div>

          {!data.active ? (
            <div className="lor-intel-all-clear">
              <div className="lor-intel-all-clear-icon"><Icon emoji="✅" size={28} /></div>
              <p>No starters on bye this week. Your lineup is clear to go.</p>
            </div>
          ) : (
            <ByePlannerContent data={data} />
          )}
        </>
      )}
    </div>
  );
}

function ByePlannerContent({ data }) {
  return (
    <div className="lor-bye-content">
      {/* Alert banner */}
      <div className={`lor-bye-alert lor-bye-alert--${data.severity}`}>
        <span className="lor-bye-alert-icon">
          {data.severity === 'high' ? '🚨' : <Icon emoji="⚠️" size={18} />}
        </span>
        <div>
          <strong>{data.bye_starters.length} starter{data.bye_starters.length > 1 ? 's' : ''} on bye this week</strong>
          <div className="lor-bye-alert-sub">
            Projected point loss: <strong>{data.projected_loss_from_byes} pts</strong>
            {' '}· With replacements: <strong>{data.projected_score_with_replacements} pts</strong>
            {' '}(was {data.projected_score_baseline} pts)
          </div>
        </div>
      </div>

      {/* Bye starters */}
      <div className="lor-bye-section">
        <div className="lor-bye-section-title">Players on bye</div>
        <div className="lor-bye-players">
          {data.bye_starters.map((p, i) => (
            <div key={i} className="lor-bye-player-chip">
              <span className="lor-bye-chip-pos">{p.position}</span>
              <span className="lor-bye-chip-name">{p.full_name}</span>
              <span className="lor-bye-chip-team">{p.team}</span>
              <span className="lor-bye-chip-pts">~{p.projected} pts lost</span>
            </div>
          ))}
        </div>
      </div>

      {/* Bench replacements */}
      {data.bench_replacements.length > 0 && (
        <div className="lor-bye-section">
          <div className="lor-bye-section-title">Bench replacements</div>
          {data.bench_replacements.map((rep, i) => (
            <div key={i} className="lor-bye-replacement-row">
              <div className="lor-bye-rep-label">
                Replace <strong>{rep.bye_player.full_name}</strong> ({rep.bye_player.position}) with:
              </div>
              {rep.best_bench.length === 0 ? (
                <div className="lor-bye-no-replace">No bench options — check the waiver wire</div>
              ) : (
                <div className="lor-bye-rep-options">
                  {rep.best_bench.map((bp, j) => (
                    <div key={j} className={`lor-bye-rep-option${j === 0 ? ' lor-bye-rep-option--best' : ''}`}>
                      {j === 0 && <span className="lor-bye-best-tag">Best available</span>}
                      <span className="lor-bye-rep-name">{bp.full_name}</span>
                      <span className="lor-bye-rep-meta">{bp.position} · {bp.team}</span>
                      <span className="lor-bye-rep-proj">{bp.projected} pts</span>
                      {bp.injury_status && (
                        <span className="lor-bye-rep-inj">{bp.injury_status}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Waiver suggestions */}
      {data.waiver_suggestions.length > 0 && (
        <div className="lor-bye-section">
          <div className="lor-bye-section-title">
            🚨 Waiver adds needed
            {data.priority_adds.length > 0 && (
              <span className="lor-bye-priority-pos">{data.priority_adds.join(', ')}</span>
            )}
          </div>
          <div className="lor-bye-waiver-list">
            {data.waiver_suggestions.map((s, i) => (
              <div key={i} className="lor-bye-waiver-add">
                <span className="lor-bye-waiver-pos">{s.position}</span>
                <span className="lor-bye-waiver-name">{s.full_name}</span>
                <span className="lor-bye-waiver-team">{s.team}</span>
                <span className="lor-bye-waiver-proj">{s.projected} proj</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="lor-generated-tag">⚙️ rules-based</div>
    </div>
  );
}
